import { useState } from "react";
import { motion } from "framer-motion";

import SectionHeading from "@/components/SectionHeading";
import { Button } from "@/components/ui/button";
import { siteConfig } from "@/content/site";

function buildMailtoPayload(fields) {
  const subject = encodeURIComponent("New inquiry from Tech Savvy Starts Here website");
  const body = encodeURIComponent(
    fields
      .map(([key, value]) => `${key}: ${value}`)
      .join("\n"),
  );

  return `mailto:${siteConfig.email}?subject=${subject}&body=${body}`;
}

export default function ContactFormSection({ content, dataTestId, sectionId = "send-inquiry" }) {
  const initialState = Object.fromEntries(content.fields.map((field) => [field.name, ""]));
  const [values, setValues] = useState(initialState);
  const [message, setMessage] = useState("");

  const handleChange = (event) => {
    const { name, value } = event.target;
    setValues((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      if (siteConfig.contactFormEndpoint) {
        const response = await fetch(siteConfig.contactFormEndpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(values),
        });

        if (!response.ok) {
          setMessage("Something went wrong. Please email us directly instead.");
          return;
        }

        setMessage(content.successMessage);
        setValues(initialState);
        return;
      }
    } catch {
      setMessage("Something went wrong. Please email us directly instead.");
      return;
    }

    window.location.href = buildMailtoPayload(Object.entries(values));
    setMessage(content.successMessage);
  };

  return (
    <section
      className="py-16 md:py-24 bg-muted/50 scroll-mt-24 md:scroll-mt-28"
      id={sectionId}
      data-testid={dataTestId}
    >
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <SectionHeading title={content.title} description={content.description} align="left" className="mb-6" />
            {content.intro ? <p className="font-body text-muted-foreground leading-relaxed">{content.intro}</p> : null}
          </motion.div>
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            onSubmit={handleSubmit}
            className="bg-card rounded-3xl p-8 shadow-soft border border-border space-y-5"
          >
            {content.fields.map((field) => (
              <label key={field.name} className="block">
                <span className="font-body text-sm font-medium text-foreground block mb-2">{field.label}</span>
                {field.type === "textarea" ? (
                  <textarea
                    name={field.name}
                    value={values[field.name]}
                    onChange={handleChange}
                    placeholder={field.placeholder}
                    required={field.required}
                    rows={5}
                    className="w-full rounded-2xl border border-border bg-background/65 px-4 py-3 font-body text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-secondary/55"
                  />
                ) : (
                  <input
                    type={field.type}
                    name={field.name}
                    value={values[field.name]}
                    onChange={handleChange}
                    placeholder={field.placeholder}
                    required={field.required}
                    className="w-full rounded-2xl border border-border bg-background/65 px-4 py-3 font-body text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-secondary/55"
                  />
                )}
              </label>
            ))}
            <Button type="submit" size="lg" className="rounded-full bg-primary hover:bg-primary/90 text-white font-medium px-8 btn-hover">
              {content.submitLabel}
            </Button>
            {message ? <p className="font-body text-sm text-muted-foreground">{message}</p> : null}
          </motion.form>
        </div>
      </div>
    </section>
  );
}
